import * as React from 'react';
import { useState } from 'react';
import { translateBlock } from 'i18n';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { capitalize } from 'helpers/helpers';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import categories from 'routes/Explore/components/Sentence/components/functions_categories.json';
import './_cellar.scss';
import SearchBar from 'ui/SearchBar/SearchBar';
import { checkStringMatch } from 'routes/Library/helpers';
import { isKeyOf } from 'helpers/types/utils';
import { Box } from '@mui/material';
import { RightType } from 'business/definitions';
import { Link } from 'react-router-dom';
import { getClientBind } from 'store/APIClient';
import { useSelector } from 'store/hooks';
import IntegrationDetails from './IntegrationDetails';
import { MANTY_COLORS } from 'business/constants';

const FILES_IMPORT = 'filesImport';

export type TableT = {
    label: string;
    value: string;
    shouldShowTable: boolean;
    isArchived?: boolean;
    rights?: RightType[];
};

type PropsDomainDetails = {
    domain: string;
    tables: TableT[];
    children?: JSX.Element | null;
};

const DomainDetails = ({
    domain,
    tables,
    children = undefined,
}: PropsDomainDetails): React.ReactElement => {
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>('');

    const subDomains: { [domain: string]: string[] } = isKeyOf(
        categories,
        domain,
    )
        ? categories[domain]
        : {};

    const isDemo = getClientBind() === 'demo';
    const isSupportUser = useSelector((state) => state.vision.isSupportUser);

    const filteredTables = tables
        .sort((a, b) => {
            if (a.label > b.label) return 1;
            if (a.label < b.label) return -1;
            return 0;
        })
        .filter((table) => checkStringMatch(table.label, search));

    const subDomainDict: { [table: string]: string } = {};
    tables.forEach((table) => {
        Object.keys(subDomains).forEach((subDomain: string) => {
            if (subDomains[subDomain].includes(table.value)) {
                subDomainDict[table.value] = subDomain;
            }
        });
    });

    return (
        <Box>
            <Box className="header-container">
                <Box className="domain-description">
                    <Box className="domain-header-wrapper">
                        <Typography variant="h4" className="title">
                            {domain !== FILES_IMPORT
                                ? capitalize(
                                      translateBlock(domain.toLowerCase()),
                                  )
                                : t('cellar.filesImport')}
                        </Typography>
                        {children}
                    </Box>

                    <Typography variant="h4" padding="0 0 2rem 2rem">
                        {t('cellar.tableList')}
                    </Typography>

                    <Box className="search-bar-wrapper">
                        <SearchBar
                            width="15rem"
                            handleChange={setSearch}
                            placeholder={t('cellar.tableSearch')}
                        />
                    </Box>
                </Box>
            </Box>
            <Box className="tables-container">
                <List disablePadding dense>
                    <ListItem className="table-row">
                        <ListItemText
                            className="column-name table-name"
                            inset
                            primary={
                                <Typography variant="h5">
                                    {t('cellar.columnNameTable')}
                                </Typography>
                            }
                        />
                        <ListItemText
                            className="column-name table-subdomain"
                            inset
                            primary={
                                <Typography variant="h5">
                                    {t('cellar.columnNameSubDomain')}
                                </Typography>
                            }
                        />
                    </ListItem>
                    {filteredTables.map(
                        (table) =>
                            table.shouldShowTable && (
                                <Link
                                    key={table.value}
                                    style={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                    }}
                                    to={`/cellar/${domain}/${table.value}`}
                                >
                                    <ListItem
                                        component="button"
                                        className="table-row"
                                        key={table.value}
                                        data-value={table.value}
                                        sx={{
                                            backgroundColor: MANTY_COLORS.white,
                                            border: 'none',
                                        }}
                                    >
                                        <ListItemText
                                            inset
                                            className="table-value"
                                            primary={
                                                <Box className="table-name">
                                                    <Tooltip
                                                        title={capitalize(
                                                            table.label,
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h5"
                                                            color={
                                                                table.isArchived
                                                                    ? 'disabled'
                                                                    : 'primary'
                                                            }
                                                        >
                                                            {capitalize(
                                                                table.label,
                                                            )}
                                                        </Typography>
                                                    </Tooltip>
                                                    <ArrowForwardIcon
                                                        color={
                                                            table.isArchived
                                                                ? 'disabled'
                                                                : 'primary'
                                                        }
                                                        className="table-icon"
                                                    />
                                                </Box>
                                            }
                                        />

                                        <ListItemText
                                            inset
                                            className="table-subdomain"
                                            primary={
                                                table.value in subDomainDict
                                                    ? translateBlock(
                                                          `${domain}.${
                                                              subDomainDict[
                                                                  table.value
                                                              ]
                                                          }`,
                                                      )
                                                    : t('cellar.unknown')
                                            }
                                        />
                                    </ListItem>
                                </Link>
                            ),
                    )}
                </List>
            </Box>
            {(isSupportUser || isDemo) && (
                <IntegrationDetails domain={domain} />
            )}
        </Box>
    );
};

export default DomainDetails;
